import * as React from 'react'
import { connect } from 'react-redux'
import { RequestContext, GetStringsInput, Logger } from 'DVWebNodeUtils'
import { Text } from 'DVWebNodeUI'
import { strings as productMetaInfoString } from '../productMetaInfo/strings'
import { strings as morePurchaseOptionsString } from '../morePurchaseOptions/strings'
import { strings as refundString } from '../refund/strings'
import { strings as episodeString } from '../episode/strings'
import { strings as redeemAndPromoLinkString } from '../redeemAndPromoLink/strings'
import { strings as customerErrorMessageStrings } from '../customerErrorMessage/strings'
import { strings as scheduleStrings } from '../schedule/strings'
import { strings as seasonSelectorStrings } from '../seasonSelector/strings'
import { strings as badgeStrings } from '../badges/strings'
import { strings as watchlistStrings } from '../watchlist/strings'
import { strings as synopsisStrings } from '../synopsis/strings'
import { strings as pageUnavailableStrings } from '../pageUnavailable/strings'
import { strings as paginationStrings } from '../collection/strings'
import { strings as playbackMessagingStrings } from '../playbackMessaging/strings'
import { strings as actionBoxStrings } from '../actionBox/strings'
import { strings as castAndCrewStrings } from '../castAndCrew/strings'
import { strings as otherFormatsStrings } from '../otherFormats/strings'
import { StringId } from './stringId'
import { DecodedText } from '../decodedText/decodedText'
import { withLogger, ILogger } from '../../context/withLogger'
import { IDetailPageState } from '../../reducers'
import { memoizedGetString } from '../../selectors/string'
import { Subtract } from '../../utils/utils'
import {
  DVStringAttrs,
  buildDvString,
  IGetString
} from '../../utils/stringUtils'

export const enum StringBundle {
  ATF,
  BTF
}

export interface ITextLocalizerProps {
  stringID: StringId
  replace?: { [key: string]: string }
  dvStringAttrs?: DVStringAttrs
  shouldDecode?: boolean
  logger: Logger
}

export const getStringIds = (bundleName: StringBundle): string[] => {
  function keys<O>(o: O) {
    return Object.keys(o) as (keyof O)[]
  }
  return keys(getDefaultStrings(bundleName)).map(k => StringId[k])
}

export const getStringsInput = (
  context: RequestContext,
  bundleName: StringBundle
) => ({ context, ids: getStringIds(bundleName) } as GetStringsInput)

export const getDefaultStrings = (
  bundleName: StringBundle
): Partial<Record<StringId, string>> => {
  const stringBundles = {
    [StringBundle.BTF]: {
      ...productMetaInfoString,
      ...morePurchaseOptionsString,
      ...refundString,
      ...episodeString,
      ...redeemAndPromoLinkString,
      ...customerErrorMessageStrings,
      ...scheduleStrings,
      ...paginationStrings,
      ...actionBoxStrings,
      ...castAndCrewStrings,
    },
    [StringBundle.ATF]: {
      ...seasonSelectorStrings,
      ...productMetaInfoString,
      ...badgeStrings,
      ...watchlistStrings,
      ...morePurchaseOptionsString,
      ...redeemAndPromoLinkString,
      ...synopsisStrings,
      ...refundString,
      ...pageUnavailableStrings,
      ...playbackMessagingStrings
    }
  }
  return stringBundles[bundleName]
}

export const getAllDefaultStrings = (): Record<StringId, string> => ({
  ...productMetaInfoString,
  ...morePurchaseOptionsString,
  ...refundString,
  ...episodeString,
  ...redeemAndPromoLinkString,
  ...customerErrorMessageStrings,
  ...scheduleStrings,
  ...seasonSelectorStrings,
  ...badgeStrings,
  ...watchlistStrings,
  ...synopsisStrings,
  ...paginationStrings,
  ...playbackMessagingStrings,
  ...pageUnavailableStrings,
  ...actionBoxStrings,
  ...castAndCrewStrings,
  ...otherFormatsStrings,
})

export interface LocalizerProps extends ILogger {
  getString: IGetString
}

type OwnProps = {
  stringID: StringId
  replace?: { [key: string]: string }
  dvStringAttrs?: DVStringAttrs
  shouldDecode?: boolean
} & ILogger

type TextLocalizerProps = LocalizerProps & OwnProps

export const ConnectedTextLocalizer = ({
  getString,
  stringID,
  replace,
  dvStringAttrs,
  shouldDecode = false
}: TextLocalizerProps) => {
  const string = getString(stringID, replace)
  const dvStringTuple = buildDvString(string, dvStringAttrs || {})
  return shouldDecode ? (
    <span>
      <DecodedText string={dvStringTuple.string} />
    </span> // TODO: not consuming the full tuple here
  ) : (
    <Text string={dvStringTuple} />
  )
}

export const withLocalization = <P extends LocalizerProps & ILogger>(
  Component: React.ComponentType<P>
): React.ComponentType<Subtract<P, LocalizerProps & ILogger>> =>
  withLogger(connect(mapStateToProps)(Component as any)) as any

const mapStateToProps = (
  state: IDetailPageState,
  ownProps: OwnProps
): { getString: IGetString } => ({
  getString: memoizedGetString(state, ownProps.logger),
})

export const TextLocalizer = withLocalization(ConnectedTextLocalizer)
